/**
 * 存储工具函数
 * 支持 SM4 加密存储和 IndexedDB 图片缓存
 */

import { openDB } from 'idb'
import { sm3, sm4 } from 'sm-crypto-v2'

const isProd = import.meta.env.PROD

/**
 * 本地存储键名
 */
let userInfoKey = ''
let userMenuKey = ''
let comdCtKey = ''
let queryProcuementKey = ''
let queryATxtKey = ''
let 结算单状态枚举键名 = ''

if (isProd) {
  userInfoKey = '09a0isdf90u'
  userMenuKey = 'asdgadgq4ta'
  comdCtKey = 'a4sb4fa17b6a'
  queryProcuementKey = 'fasdgayg7yqyafha'
  queryATxtKey = 'queryATxtKeyProd'
  结算单状态枚举键名 = 'settlementStatusEnumProd'
} else {
  userInfoKey = 'accountInfo'
  userMenuKey = 'userMenuKey'
  comdCtKey = 'comdCtKey'
  queryProcuementKey = 'queryProcuementKey'
  queryATxtKey = 'queryATxtKey'
  结算单状态枚举键名 = '2392y93492342hs'
}

/**
 * 常量数据
 */
export const constData = {
  sm4Def: '0123456789abcdeffedcba9876543210'
}

/**
 * 图片服务器前缀
 */
export const baseImgUrl = {
  jd: 'https://img13.360buyimg.com/n1/',
  me: 'https://imgs.promise.pink/',
  gxjn: 'https://imgs.promise.pink/'
}

// 打开 IndexedDB 数据库
const dbPromise = openDB('image-cache', 1, {
  upgrade(db) {
    db.createObjectStore('images', { keyPath: 'url' })
  }
})

/**
 * 存储图片到 IndexedDB
 */
export async function setImageToCache(url: string, blob: Blob): Promise<void> {
  const db = await dbPromise
  await db.put('images', { url, blob })
}

/**
 * 从 IndexedDB 获取图片
 */
export async function getImageFromCache(url: string): Promise<Blob | undefined> {
  const db = await dbPromise
  const item = await db.get('images', url)
  return item?.blob
}

/**
 * 获取完整图片路径（带缓存）
 */
export async function getImgUrl(opt: { type?: 'jd' | 'me' | 'gxjn'; url: string }): Promise<string> {
  if (!opt.url) return ''
  
  let lurl: string
  
  if (opt.url.startsWith('http://') || opt.url.startsWith('https://')) {
    lurl = opt.url
  } else {
    lurl = baseImgUrl[opt.type || 'me'] + opt.url
  }
  
  const hash = sm3(lurl)
  const blob = await getImageFromCache(hash)
  
  if (blob) {
    const blobUrl = URL.createObjectURL(blob)
    return blobUrl
  }
  
  // 异步加载图片并缓存
  setTimeout(async () => {
    try {
      const response = await fetch(lurl)
      if (!response.ok) {
        return
      }
      const blob = await response.blob()
      await setImageToCache(hash, blob)
    } catch (error) {
      console.error('Failed to fetch image:', error)
    }
  }, 1000)
  
  return lurl
}

/**
 * 清除所有本地存储的缓存
 */
export function clearAllLocalStorage(): void {
  try {
    localStorage.clear()
  } catch (error) {
    console.error('清除 localStorage 时发生错误:', error)
  }
}

/**
 * 存储数据（支持加密）
 */
function setLocalStorage(key: string, value: any): void {
  try {
    const valueToStore = JSON.stringify({ v: value })
    
    if (isProd) {
      localStorage.setItem(key, sm4.encrypt(valueToStore, constData.sm4Def))
    } else {
      localStorage.setItem(key, valueToStore)
    }
  } catch (error) {
    console.error('设置 localStorage 时发生错误:', error)
  }
}

/**
 * 读取数据（支持解密）
 */
function getLocalStorage<T>(key: string): T | null {
  const value = localStorage.getItem(key)
  
  try {
    if (value) {
      if (isProd) {
        return JSON.parse(sm4.decrypt(value, constData.sm4Def)).v as T
      } else {
        return JSON.parse(value).v as T
      }
    }
  } catch (error) {
    console.error('读取 localStorage 时发生错误:', error)
    clearAllLocalStorage()
    return null
  }
  
  return null
}

/**
 * 删除存储
 */
function removeLocalStorage(key: string): void {
  localStorage.removeItem(key)
}

/**
 * 存储用户信息
 */
export function setLocUserInfo(data: any): void {
  setLocalStorage(userInfoKey, data || [])
}

/**
 * 获取用户信息
 */
export function getLocUserInfo(): any {
  return getLocalStorage<any>(userInfoKey)
}

/**
 * 通用获取存储方法
 */
export function getLocStorage<T>(key: string): T | null {
  return getLocalStorage<T>(key)
}

/**
 * 通用设置存储方法
 */
export function setLocStorage<T>(key: string, data: T): void {
  setLocalStorage(key, data)
}

/**
 * 通用删除存储方法
 */
export function removeLocStorage(key: string): void {
  removeLocalStorage(key)
}

/**
 * 存储菜单信息
 */
export function setUserMenu(data: any): void {
  setLocalStorage(userMenuKey, data || [])
}

/**
 * 获取菜单信息
 */
export function getUserMenu(): any {
  return getLocalStorage<any>(userMenuKey) || []
}

/**
 * 存储商品分类信息
 */
export function setComdCt(data: any): void {
  setLocalStorage(comdCtKey, data || [])
}

/**
 * 获取商品分类信息
 */
export function getComdCt(): any[] {
  return getLocalStorage<any[]>(comdCtKey) || []
}

/**
 * 存储查询采购列表条件
 */
export function setQueryProcuement(data: any): void {
  setLocalStorage(queryProcuementKey, data || [])
}

/**
 * 查询采购列表条件
 */
export function getQueryProcuement(): any {
  return getLocalStorage<any>(queryProcuementKey)
}

/**
 * 存储查询 aTxt 列表条件
 */
export function setQueryATxt(data: any): void {
  setLocalStorage(queryATxtKey, data || [])
}

/**
 * 查询 aTxt 列表条件
 */
export function getQueryATxt(): any {
  return getLocalStorage<any>(queryATxtKey)
}

/**
 * 存储结算订单状态枚举
 */
export function 存储结算单状态枚举(data: any): void {
  setLocalStorage(结算单状态枚举键名, data || [])
}

/**
 * 获取结算订单状态枚举
 */
export function 本地结算单状态枚举(): any {
  return getLocalStorage<any>(结算单状态枚举键名)
}
